import { BankStatementRow, Debt } from '../types';

export interface TransferMatch {
  debitRowId: string;
  creditRowId: string;
}

const MS_PER_DAY = 86400000;

/**
 * Only two pairings are representable as a single real `type: 'transfer'`
 * transaction, per TransactionModal.tsx's own shape: bank account -> bank
 * account, or a line-of-credit draw -> bank account (`toBankAccountId` is
 * always a bank_accounts doc, never a debt -- there's no "transfer to a
 * credit card" concept in this schema, that's a separate debt_payment).
 * A credit-card leg, or anything else, is never eligible -- those rows
 * still import fine as their own normal transaction, just not merged.
 */
function isEligibleSource(row: BankStatementRow, debts: Debt[]): boolean {
  if (row.accountType === 'bank_account') return true;
  if (row.accountType === 'debt') {
    const debt = debts.find(d => d.id === row.accountId);
    return debt?.type === 'line_of_credit';
  }
  return false;
}
function isEligibleDestination(row: BankStatementRow): boolean {
  return row.accountType === 'bank_account';
}

/**
 * Pairs opposite-direction rows across different accounts, household-wide,
 * among currently-pending rows only (status 'new'/'possible_duplicate' --
 * never already-imported/excluded/duplicate/already-paired). Exact amount
 * to the cent, date within 3 days. Ties in date-closeness are left
 * unpaired rather than guessed at.
 */
export function findTransferMatches(rows: BankStatementRow[], debts: Debt[]): TransferMatch[] {
  const candidates = rows.filter(r => r.status === 'new' || r.status === 'possible_duplicate');
  const sorted = [...candidates].sort((a, b) => a.date.getTime() - b.date.getTime());
  const consumed = new Set<string>();
  const matches: TransferMatch[] = [];

  for (const row of sorted) {
    if (consumed.has(row.id!)) continue;
    if (row.direction === 'debit' && !isEligibleSource(row, debts)) continue;
    if (row.direction === 'credit' && !isEligibleDestination(row)) continue;

    const oppositeDirection = row.direction === 'debit' ? 'credit' : 'debit';
    const potential = sorted.filter(r => {
      if (r.id === row.id || consumed.has(r.id!)) return false;
      if (r.direction !== oppositeDirection) return false;
      if (r.accountId === row.accountId) return false;
      if (Math.abs(r.amount - row.amount) > 0.001) return false;
      if (Math.abs(r.date.getTime() - row.date.getTime()) / MS_PER_DAY > 3) return false;
      if (r.direction === 'debit' && !isEligibleSource(r, debts)) return false;
      if (r.direction === 'credit' && !isEligibleDestination(r)) return false;
      return true;
    });
    if (potential.length === 0) continue;

    let minDelta = Infinity;
    let best: BankStatementRow[] = [];
    for (const p of potential) {
      const delta = Math.abs(p.date.getTime() - row.date.getTime());
      if (delta < minDelta) { minDelta = delta; best = [p]; }
      else if (delta === minDelta) best.push(p);
    }
    if (best.length !== 1) continue; // ambiguous tie -- leave both unpaired

    const match = best[0];
    consumed.add(row.id!);
    consumed.add(match.id!);
    matches.push({
      debitRowId: row.direction === 'debit' ? row.id! : match.id!,
      creditRowId: row.direction === 'credit' ? row.id! : match.id!,
    });
  }

  return matches;
}
